import { FocusEventHandler, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { ListSubheader, Typography } from "@material-ui/core";
import SelectField from "components/Forms/SelectField";
import AlertMsg from "components/AlertMsg";
import { FieldSkeleton } from "components/SkeletonLoading";
import openAuthPopup, {
  isClosedByUser,
  isDifferentAccount,
} from "pages/Integrations/Accounts/openAuthPopup";
import useStorageAccounts from "../useStorageAccounts";
import OptionLabel from "./OptionLabel";
import SyncModal from "./SyncModal";
import AddModal from "./AddModal";
import { StyledDivider, StyledMenuItem } from "./styled";
import {
  StorageAccountStatus,
  getCloudStorageStatus,
  isIndividual,
} from "./utils";

import { TFunction } from "i18next";
import { StorageProviderWithAuth, StorageProvider } from "@dashboard-v3/api";
import {
  StorageAccountUpdate,
  StorageAccountItem,
  StorageAccountValue,
} from "../types";

type Props = {
  label?: string;
  error?: boolean;
  helperText?: string;
  isEditing: boolean;
  disabled?: boolean;
  required?: boolean;
  allowEmpty?: boolean;
  allowOrgAccounts?: boolean;
  "data-testid"?: string;
  excludedProviders?: Array<StorageProvider>;
  /* Accounts to disable beyond their own sync state, e.g. synced accounts the
     consumer could not use anyway */
  disabledAccountIds?: string[];
  value: StorageAccountValue;
  onChange: (change: StorageAccountUpdate) => void;
  onBlur?: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  /* Emitted after an account is successfully re-authorized or re-synced */
  onReAuthSuccess?: () => void;
};

type State = {
  openSyncModal: boolean;
  openAddModal: boolean;
  selectedItem: StorageAccountItem | null;
};

function initState() {
  return { openSyncModal: false, openAddModal: false, selectedItem: null };
}

export default function StorageAccountSelector(props: Props) {
  const { value, onChange, isEditing, disabled } = props;
  const { t } = useTranslation("components");
  const { enqueueSnackbar } = useSnackbar();
  const [state, setState] = useState<State>(initState);
  const {
    items: fetchedItems,
    isLoading,
    reFetchOptions,
  } = useStorageAccounts({
    includeOrgAccounts: props.allowOrgAccounts,
    excludedProviders: props.excludedProviders,
  });
  const items = applyDisabledAccounts(fetchedItems, props.disabledAccountIds);
  const accountStatus = getCloudStorageStatus(value, items);
  const warning = getWarningMessage(accountStatus, value, t);
  const showOutOfSyncWarning =
    isEditing && accountStatus !== StorageAccountStatus.OK;

  function updateState(update: Partial<State>) {
    setState(prev => ({ ...prev, ...update }));
  }

  function renderValue(key: unknown) {
    if (accountStatus === StorageAccountStatus.NOT_FOUND) {
      return "id" in value ? "" : value.userId;
    }
    if (!key) return "";

    const item = items.find(isSameKey(key as string));
    return t(item?.option?.title);
  }

  function storageAccountToKey(value: StorageAccountValue): string {
    if (!value) return "";
    if (accountStatus === StorageAccountStatus.NOT_FOUND) {
      return "id" in value ? "" : value.userId;
    }
    const item = items?.find(isSameStorage(value));

    return item?.key || "";
  }

  function handleStorageAccount(e) {
    if (e.target.value === "addAccount") {
      return updateState({ openAddModal: true });
    }

    const itemKey = e.target.value as string;
    const item = items.find(isSameKey(itemKey));

    onChange(item.storageAccount);
  }

  async function handleAccountAuth(
    provider: StorageProviderWithAuth,
    user: string
  ) {
    try {
      await openAuthPopup(provider, user, "reauthorize");
      reFetchOptions();
      props.onReAuthSuccess?.();
      enqueueSnackbar(t("storageAccounts.reAuth.success"), {
        variant: "success",
      });
    } catch (error) {
      if (isClosedByUser(error)) return;
      if (isDifferentAccount(error)) {
        return enqueueSnackbar(
          t("storageAccounts.reAuth.matchError", { user }),
          { variant: "error" }
        );
      }
      enqueueSnackbar(t("storageAccounts.reAuth.error"), { variant: "error" });
    }
  }

  async function handleWarningAction() {
    // In id mode the account data comes from the matched item; a NOT_FOUND
    // value has no item to resolve, so the action can only bail out.
    const storageAccount =
      "id" in value ? items?.find(isSameStorage(value))?.storageAccount : value;

    if (!storageAccount) return;

    const { provider, userId } = storageAccount;
    await handleAccountAuth(provider as StorageProviderWithAuth, userId);
  }

  const handleOnLabelClick = async (selectedItem: StorageAccountItem) => {
    const { storageAccount } = selectedItem;
    const { authentication, provider, userId } = storageAccount;

    if (isIndividual(authentication)) {
      await handleAccountAuth(provider as StorageProviderWithAuth, userId);
    } else {
      updateState({ selectedItem, openSyncModal: true });
    }
  };

  function handleAccountChange() {
    reFetchOptions();
    props.onReAuthSuccess?.();
    updateState({ selectedItem: null, openSyncModal: false });
  }

  if (isLoading) {
    return <FieldSkeleton />;
  }

  return (
    <>
      <SelectField<StorageAccountItem>
        name="storageAccount"
        label={props.label}
        options={items}
        renderValue={renderValue}
        value={storageAccountToKey(value)}
        onChange={handleStorageAccount}
        disabled={disabled}
        onBlur={props.onBlur}
        error={props.error}
        helperText={props.helperText}
        required={props.required}
        allowEmpty={props.allowEmpty}
        data-testid={props["data-testid"]}
        renderOption={item => {
          const { option, key } = item;

          const renderedItem = (
            <StyledMenuItem key={key} value={key} disabled={!option.selectable}>
              {item.storageAccount && (
                <OptionLabel item={item} onLabelClick={handleOnLabelClick} />
              )}
              <Typography>{t(option.title)}</Typography>
            </StyledMenuItem>
          );

          if (!option.showSeparator) return renderedItem;

          return [
            props.allowOrgAccounts && (
              <ListSubheader key="SEPARATOR-TOP">
                <StyledDivider />
              </ListSubheader>
            ),

            <StyledMenuItem key="addAccount" value="addAccount">
              <Typography>{t("storageAccounts.option.addAccount")}</Typography>
            </StyledMenuItem>,

            <ListSubheader key="SEPARATOR_BOTTOM">
              <StyledDivider />
            </ListSubheader>,
            renderedItem,
          ];
        }}
      />
      {showOutOfSyncWarning && (
        <AlertMsg actionBtn={warning.buttonText} onClick={handleWarningAction}>
          {warning.message}
        </AlertMsg>
      )}
      {state.selectedItem && (
        <SyncModal
          isOpen={state.openSyncModal}
          selectedAccount={state.selectedItem}
          onChange={handleAccountChange}
          onClose={() => updateState({ openSyncModal: false })}
        />
      )}
      <AddModal
        isOpen={state.openAddModal}
        excludedProviders={props.excludedProviders}
        onClose={() => updateState({ openAddModal: false })}
        onAccountAdd={async change => {
          await reFetchOptions();
          onChange(change);
        }}
      />
    </>
  );
}

const isSameKey = (key: string) => (item: StorageAccountItem) => {
  return item.key === key;
};

function applyDisabledAccounts(
  items: StorageAccountItem[],
  disabledAccountIds: string[] = []
) {
  if (!items || !disabledAccountIds.length) return items;

  return items.map(item => {
    const { id } = item.storageAccount || {};
    if (!id || !disabledAccountIds.includes(id)) return item;

    // Only vetoes the option; needsReAuth stays as the backend reported it,
    // so the re-auth affordances are not offered for unrelated failures.
    return { ...item, option: { ...item.option, selectable: false } };
  });
}

const isSameStorage =
  (value: StorageAccountValue) => (item: StorageAccountItem) => {
    if ("id" in value) return item.storageAccount?.id === value.id;

    const sameProvider =
      item.storageAccount &&
      item.storageAccount.authentication === value.authentication &&
      item.storageAccount.provider === value.provider;

    if (value.authentication === "ORGANIZATION") return sameProvider;

    return sameProvider && item.storageAccount.userId === value.userId;
  };

const getWarningMessage = (
  status: StorageAccountStatus,
  value: StorageAccountValue,
  t: TFunction
) => {
  const warning = { buttonText: "", message: "" };

  if (status === StorageAccountStatus.OUT_OF_SYNC) {
    warning.buttonText = t("syncAgain");
    warning.message = t(`storageAccounts.warnings.${status}`);
  }

  if (status === StorageAccountStatus.NOT_FOUND) {
    // An id-only value that is NOT_FOUND cannot be resolved to an account,
    // so it gets a message without provider or account.
    warning.buttonText = t("storageAccounts.option.addAccount");
    warning.message =
      "id" in value
        ? t("storageAccounts.warnings.NOT_FOUND_BY_ID")
        : t("storageAccounts.warnings.NOT_FOUND", {
            account: value.userId,
            provider: t(`common:integration.provider.${value.provider}`),
          });
  }

  return warning;
};
